/**
 * Chat — in-app messaging with two sections:
 *   1. Group threads — one per class+section (parents + teachers + admin)
 *   2. Direct messages — parent ↔ teacher / branch_admin
 *
 * Attachments: PDF, JPEG, PNG, MP4 / MOV.  Images are auto-compressed on
 * the client before upload (capped at 1600px long edge, jpeg quality 0.82)
 * so we don't ship 12 MP iPhone photos through the slowest parent's link.
 *
 * Polling refresh every 12 s — keeps the implementation footprint tiny
 * vs. websockets while still feeling live.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Panel, Pill, EmptyState, Avatar } from "@/components/Bits";
import { toast } from "sonner";
import {
  ChatCircleText, UsersThree, PaperPlaneTilt, Paperclip, ImageSquare,
  FilePdf, FilmStrip, X, CheckCircle, ArrowLeft, Plus, MagnifyingGlass,
} from "@phosphor-icons/react";

const POLL_INTERVAL_MS = 12_000;
const IMAGE_MAX_EDGE = 1600;
const IMAGE_QUALITY = 0.82;

// ─────────────────────────────────────────────────────────────────────
// Compress an image File client-side using a canvas. Returns the
// compressed File. Falls back to the original on failure.
// ─────────────────────────────────────────────────────────────────────
async function compressImage(file) {
  if (!file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file; // GIFs aren't worth re-encoding
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, IMAGE_MAX_EDGE / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", IMAGE_QUALITY));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.(png|webp|gif)$/i, ".jpg"),
      { type: "image/jpeg" });
  } catch (e) {
    return file;
  }
}

export default function Chat() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // {thread, messages}
  const [section, setSection] = useState("groups"); // groups | dms
  const [directory, setDirectory] = useState([]);
  const [showDir, setShowDir] = useState(false);
  const [dirSearch, setDirSearch] = useState("");

  // Class group quick-open (parents auto, admins pick)
  const [classOptions, setClassOptions] = useState([]);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/chat/threads");
      setThreads(data);
    } catch (e) {
      // Silent — keep stale list
    }
  };

  useEffect(() => {
    loadThreads();
    api.get("/chat/directory").then(({ data }) => setDirectory(data.items || [])).catch(() => {});
    // Build class options for "Open class chat" quick action
    (async () => {
      if (user?.role === "parent") {
        const { data } = await api.get("/students").catch(() => ({ data: [] }));
        const classes = Array.from(new Set(data.map((s) => s.class_name).filter(Boolean)));
        setClassOptions(classes);
      } else if (["teacher", "branch_admin", "centre_head", "principal"].includes(user?.role)) {
        const { data } = await api.get("/students").catch(() => ({ data: [] }));
        const classes = Array.from(new Set(data.map((s) => s.class_name).filter(Boolean)));
        setClassOptions(classes);
      }
    })();
  }, [user?.role]);

  useEffect(() => {
    const id = setInterval(() => {
      loadThreads();
      if (active?.thread?.thread_id) loadMessages(active.thread.thread_id, true);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [active?.thread?.thread_id]); // eslint-disable-line

  const loadMessages = async (threadId, silent = false) => {
    try {
      const { data } = await api.get(`/chat/threads/${threadId}/messages`);
      setActive({ thread: data.thread, messages: data.messages });
      await api.post(`/chat/threads/${threadId}/read`).catch(() => {});
      if (!silent) loadThreads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open thread");
    }
  };

  const openClassThread = async (cls) => {
    try {
      const { data } = await api.post("/chat/threads/group/class", { class_name: cls });
      await loadMessages(data.thread_id);
      setSection("groups");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open class chat");
    }
  };

  const startDm = async (otherUserId) => {
    try {
      const { data } = await api.post("/chat/threads/dm", { other_user_id: otherUserId });
      await loadMessages(data.thread_id);
      setSection("dms");
      setShowDir(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start DM");
    }
  };

  const groups = threads.filter((t) => t.kind === "group");
  const dms = threads.filter((t) => t.kind === "dm");
  const visible = section === "groups" ? groups : dms;
  const totalUnread = threads.reduce((acc, t) => acc + (t.unread || 0), 0);
  const filteredDir = useMemo(() => (directory || []).filter((d) =>
    !dirSearch || d.name.toLowerCase().includes(dirSearch.toLowerCase())), [directory, dirSearch]);

  return (
    <div className="space-y-5" data-testid="chat-page">
      <PageHeader eyebrow="Messages" title="In-app communication"
        subtitle="Class group chats + private 1-on-1 messages with teachers and admin. Strictly branch-only."
        action={totalUnread > 0 ? <Pill tone="terracotta">{totalUnread} unread</Pill> : null} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Sidebar */}
        <Panel className="p-0 overflow-hidden" testid="chat-sidebar">
          <div className="flex" data-testid="chat-sections">
            {[
              { id: "groups", label: "Class groups", icon: UsersThree, count: groups.length },
              { id: "dms", label: "Direct", icon: ChatCircleText, count: dms.length },
            ].map((s) => {
              const Icon = s.icon;
              const active_ = section === s.id;
              return (
                <button key={s.id} onClick={() => setSection(s.id)}
                  data-testid={`chat-section-${s.id}`}
                  className={`flex-1 px-4 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition border-b-2 ${
                    active_ ? "border-terracotta text-terracotta" : "border-transparent text-ink-soft hover:bg-paper"
                  }`}>
                  <Icon weight={active_ ? "fill" : "duotone"} size={16} />
                  {s.label}
                  <span className="text-xs">({s.count})</span>
                </button>
              );
            })}
          </div>

          {section === "groups" && classOptions.length > 0 && (
            <div className="p-3 border-b border-border">
              <div className="text-xs font-semibold text-ink-soft uppercase tracking-wider mb-1.5">
                Open class chat
              </div>
              <div className="flex flex-wrap gap-1.5">
                {classOptions.map((cls) => (
                  <button key={cls} onClick={() => openClassThread(cls)}
                    data-testid={`open-class-${cls}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-mustard-soft border border-mustard/30 hover:bg-mustard">
                    {cls}
                  </button>
                ))}
              </div>
            </div>
          )}

          {section === "dms" && (
            <button onClick={() => setShowDir(true)} data-testid="new-dm-btn"
              className="m-3 px-3 py-2 text-sm font-semibold flex items-center gap-1.5 rounded-full bg-terracotta text-white hover:bg-terracotta-hover">
              <Plus weight="bold" /> New message
            </button>
          )}

          <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
            {visible.length === 0 ? (
              <div className="p-6">
                <EmptyState title={`No ${section === "groups" ? "groups" : "messages"} yet`}
                  hint={section === "groups" ? "Open a class chat above to get started." : "Start a new conversation."} />
              </div>
            ) : visible.map((t) => (
              <button key={t.thread_id} onClick={() => loadMessages(t.thread_id)}
                data-testid={`thread-row-${t.thread_id}`}
                className={`w-full text-left p-3 hover:bg-paper transition ${
                  active?.thread?.thread_id === t.thread_id ? "bg-mustard-soft" : ""
                }`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm truncate">
                    {t.title || (t.participants || []).find((p) => p.user_id !== user.user_id)?.name || "Conversation"}
                  </div>
                  {t.unread > 0 && (
                    <span className="ml-2 text-[10px] font-bold bg-terracotta text-white px-1.5 py-0.5 rounded-full">
                      {t.unread}
                    </span>
                  )}
                </div>
                <div className="text-xs text-ink-soft truncate mt-0.5">
                  {t.last_message_preview || (t.kind === "group" ? "Class group · be the first to say hi" : "Say hello")}
                </div>
              </button>
            ))}
          </div>
        </Panel>

        {/* Right pane: thread viewer */}
        <div className="lg:col-span-2">
          {active ? (
            <ChatView thread={active.thread} messages={active.messages} me={user}
              onSend={async (body, attachments) => {
                try {
                  await api.post(`/chat/threads/${active.thread.thread_id}/messages`,
                    { body, attachments });
                  await loadMessages(active.thread.thread_id);
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
                }
              }}
              onBack={() => setActive(null)} />
          ) : (
            <Panel className="p-10 text-center" testid="chat-empty">
              <ChatCircleText weight="duotone" size={56} className="mx-auto text-mustard mb-2" />
              <p className="text-ink-soft">Pick a chat on the left to start reading.</p>
            </Panel>
          )}
        </div>
      </div>

      {showDir && (
        <DirectoryModal items={filteredDir} search={dirSearch} setSearch={setDirSearch}
          onPick={startDm} onClose={() => setShowDir(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function ChatView({ thread, messages, me, onSend, onBack }) {
  const [draft, setDraft] = useState("");
  const [attaching, setAttaching] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const out = [];
    for (const f of files.slice(0, 5)) {
      let toUpload = f;
      if (f.type.startsWith("image/")) {
        toUpload = await compressImage(f);
      }
      if (toUpload.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} is over 25 MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", toUpload);
      try {
        const { data } = await api.post("/files/upload", fd,
          { headers: { "Content-Type": "multipart/form-data" }, params: { folder: "chat" } });
        out.push({
          file_id: data.file_id, url: data.url,
          name: f.name, size: toUpload.size,
          content_type: toUpload.type || f.type,
        });
      } catch (err) {
        toast.error(`${f.name}: upload failed`);
      }
    }
    setAttaching((cur) => [...cur, ...out]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAttachment = (idx) => {
    setAttaching((cur) => cur.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!draft.trim() && attaching.length === 0) return;
    setSending(true);
    await onSend(draft.trim(), attaching);
    setDraft(""); setAttaching([]); setSending(false);
  };

  return (
    <Panel className="p-0 overflow-hidden flex flex-col h-[600px]" testid="chat-view">
      <div className="p-4 border-b border-border flex items-center gap-3">
        <button onClick={onBack} className="lg:hidden p-1.5 rounded-lg hover:bg-paper"
          data-testid="chat-back">
          <ArrowLeft />
        </button>
        <div>
          <div className="font-heading font-bold">
            {thread.title || (thread.participants || []).find((p) => p.user_id !== me.user_id)?.name || "Conversation"}
          </div>
          <div className="text-xs text-ink-soft">
            {thread.kind === "group"
              ? `${(thread.members || []).length} members · class chat`
              : "Private message"}
          </div>
        </div>
      </div>

      <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-2"
        data-testid="chat-messages">
        {(!messages || messages.length === 0) ? (
          <div className="text-center text-sm text-ink-soft py-10">
            {thread.kind === "group" ? "No messages yet — say hi to the class!" : "No messages yet."}
          </div>
        ) : messages.map((m) => {
          const mine = m.sender_id === me.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}
              data-testid={`msg-${m.message_id}`}>
              <div className={`max-w-[78%] rounded-2xl px-3 py-2 ${
                mine ? "bg-terracotta text-white" : "bg-paper border border-border text-ink"
              }`}>
                {!mine && (
                  <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-0.5">
                    {m.sender_name} · {m.sender_role}
                  </div>
                )}
                {m.body && <div className="text-sm whitespace-pre-wrap">{m.body}</div>}
                {(m.attachments || []).map((a) => (
                  <AttachmentTile key={a.file_id} att={a} mine={mine} />
                ))}
                <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-ink-soft"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {attaching.length > 0 && (
        <div className="border-t border-border bg-paper-alt p-2 flex gap-2 overflow-x-auto"
          data-testid="chat-attaching">
          {attaching.map((a, i) => (
            <div key={i} className="relative shrink-0 w-32 p-2 rounded-xl bg-white border border-border text-xs">
              <button onClick={() => removeAttachment(i)} className="absolute -top-1 -right-1 bg-ink text-white rounded-full p-0.5"
                data-testid={`attach-remove-${i}`}>
                <X size={10} weight="bold" />
              </button>
              <div className="flex items-center gap-1 text-ink-soft mb-1">
                <AttachmentIcon ctype={a.content_type} size={12} />
                <span className="truncate flex-1">{a.name}</span>
              </div>
              <div className="text-[10px] text-ink-soft">{Math.round(a.size / 1024)} kB</div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="border-t border-border p-3 flex items-center gap-2">
        <input ref={fileRef} type="file" multiple onChange={onPickFiles}
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,video/mp4,video/quicktime"
          className="hidden" data-testid="chat-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()}
          className="p-2 rounded-full hover:bg-paper" data-testid="chat-attach-btn"
          title="Attach (≤ 25 MB · PDF/JPEG/PNG/MP4)">
          <Paperclip size={20} weight="duotone" />
        </button>
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 h-11 px-4 rounded-full border border-border bg-white"
          data-testid="chat-input" />
        <button disabled={sending || (!draft.trim() && attaching.length === 0)}
          type="submit" data-testid="chat-send"
          className="px-4 py-2 rounded-full bg-terracotta text-white font-semibold disabled:opacity-60 flex items-center gap-1.5">
          <PaperPlaneTilt weight="bold" /> Send
        </button>
      </form>
    </Panel>
  );
}

// ─────────────────────────────────────────────────────────────────────
function AttachmentIcon({ ctype, size = 14 }) {
  if (ctype?.startsWith("image/")) return <ImageSquare size={size} weight="duotone" />;
  if (ctype?.startsWith("video/")) return <FilmStrip size={size} weight="duotone" />;
  if (ctype === "application/pdf") return <FilePdf size={size} weight="duotone" />;
  return <Paperclip size={size} weight="duotone" />;
}

function AttachmentTile({ att, mine }) {
  const url = att.url?.startsWith("/api") ? `${API_BASE}${att.url}` : att.url;
  if (att.content_type?.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mt-1.5">
        <img src={url} alt={att.name} className="max-h-56 rounded-xl"
          data-testid={`att-img-${att.file_id}`} />
      </a>
    );
  }
  if (att.content_type?.startsWith("video/")) {
    return (
      <video controls preload="metadata" className="mt-1.5 max-h-56 rounded-xl"
        data-testid={`att-vid-${att.file_id}`}>
        <source src={url} type={att.content_type} />
      </video>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer"
      className={`mt-1.5 flex items-center gap-2 rounded-xl px-3 py-2 text-xs ${
        mine ? "bg-white/15" : "bg-mustard-soft text-ink"
      }`} data-testid={`att-pdf-${att.file_id}`}>
      <AttachmentIcon ctype={att.content_type} size={16} />
      <span className="truncate flex-1">{att.name}</span>
      <span className="opacity-70">{Math.round((att.size || 0) / 1024)} kB</span>
    </a>
  );
}

// ─────────────────────────────────────────────────────────────────────
function DirectoryModal({ items, search, setSearch, onPick, onClose }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-6"
      onClick={onClose}>
      <div className="bg-white rounded-3xl border border-border w-full max-w-md max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="chat-directory">
        <div className="p-4 border-b border-border">
          <h3 className="font-heading text-lg font-bold">Start a private message</h3>
          <div className="relative mt-3">
            <MagnifyingGlass className="absolute top-1/2 -translate-y-1/2 left-3 text-ink-soft" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name…" data-testid="chat-dir-search"
              className="w-full h-10 pl-10 pr-3 rounded-full bg-white border border-border" />
          </div>
        </div>
        <div className="divide-y divide-border">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-ink-soft">No one matches.</div>
          ) : items.map((u) => (
            <button key={u.user_id} onClick={() => onPick(u.user_id)}
              data-testid={`chat-dir-pick-${u.user_id}`}
              className="w-full p-3 text-left hover:bg-paper flex items-center gap-3">
              <Avatar name={u.name} />
              <div>
                <div className="font-semibold text-sm">{u.name}</div>
                <div className="text-xs text-ink-soft capitalize">{u.role.replace("_", " ")}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
